import type { Address, BaseMoney, CustomFieldsType, LineItem } from "@graphql/generated/types";
import { Attributes, CustomAttributes } from "@lib/enums/ProductAttributes";
import type { MoneyProps } from "@ui/components/data-display/money/Money";
import { PRODUCT_IMAGE_PLACEHOLDER_URL } from "@ui/components/media-and-icons/image/imagePaths";
import { getProductAttributeValue } from "./attributesUtils";
import type { StoreData } from "@components/store-locator/controller";
import { activeSiteConfig } from "@config/site/site-config";
import { roundPrice } from "./moneyUtils";

export type ShapedCustomField = {
    name: string;
    value: string;
};

export const getImageData = (item: LineItem, imageLabel: Attributes = Attributes.mainImage) => {
    const images = item?.variant?.images?.length
        ? item.variant.images
        : [
              {
                  url: PRODUCT_IMAGE_PLACEHOLDER_URL,
                  label: "placeholder",
              },
          ];
    return images.find((x) => x.label === imageLabel) ?? images[0];
};

export const getLineItemAttribute = (item: LineItem, name: Attributes, locale?: string): any => {
    return getProductAttributeValue(item?.variant?.attributesRaw, name, locale);
};

export const getPriceBefore = (
    item: LineItem,
    locale: string,
    forOnePiece?: boolean
): MoneyProps => {
    const discountedPrice = forOnePiece
        ? getCustomField(item.price, CustomAttributes.ADJUSTED_PRICE)
        : getCustomField(item, CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE);
    if (!discountedPrice) {
        return null;
    }
    const value = forOnePiece
        ? getCustomField(item.price, CustomAttributes.RECOMMENDED_PRICE)
        : getCustomField(item, CustomAttributes.LINE_ITEM_PRICE);
    return value ? mapToMoney(value, locale) : null;
};

export const getPriceNow = (item: LineItem, locale: string, forOnePiece?: boolean): MoneyProps => {
    const rawDiscountedPrice = forOnePiece
        ? item?.price?.discounted?.value
        : getCustomField(item, CustomAttributes.LINE_ITEM_DISCOUNTED_PRICE);

    const discountedPrice =
        rawDiscountedPrice && activeSiteConfig.roundingUnit
            ? {
                  ...rawDiscountedPrice,
                  centAmount: roundPrice(rawDiscountedPrice.centAmount, "floor", item.quantity),
              }
            : rawDiscountedPrice;
    const price = forOnePiece
        ? item?.price?.value
        : getCustomField(item, CustomAttributes.LINE_ITEM_PRICE);
    const value = discountedPrice ?? price;
    return mapToMoney(value, locale);
};

export const getCustomField = <T extends { custom?: CustomFieldsType }>(
    item: T,
    fieldName: string,
    key?: string
) => {
    const field = item?.custom?.customFieldsRaw?.find((field) => field.name === fieldName);
    if (!field) return null;
    if (!key) return field.value;
    return field.value[key] ?? null;
};

const transformFieldValue = (fieldValue: unknown): ShapedCustomField["value"] => {
    if (!fieldValue) {
        return JSON.stringify("");
    }

    return JSON.stringify(fieldValue);
};

export const shapeCustomField = (
    fieldName: string,
    fieldValue?: unknown
): ShapedCustomField | undefined => {
    if (fieldValue) {
        return {
            name: fieldName,
            value: transformFieldValue(fieldValue),
        };
    }
};

export const shapeContactDetailsCustomFields = (billingAddress: Address) => {
    const customFields: Record<string, unknown>[] = [];
    const vatField = getCustomField(billingAddress, "address_CF_VatId");

    if (vatField) {
        customFields.push(shapeCustomField("address_CF_VatId", vatField));
    }

    const isCompanyField = getCustomField(billingAddress, "address_CF_IsCompany");
    if (isCompanyField) {
        customFields.push(shapeCustomField("address_CF_IsCompany", isCompanyField));
    }

    return customFields;
};

export const shapeParcelShopCustomFields = (parcelShopData: StoreData) => {
    const customFields = [];
    if (parcelShopData) {
        customFields.push(shapeCustomField("address_CF_ParcelShopId", parcelShopData?.storeId));
        customFields.push(
            shapeCustomField("address_CF_ParcelShopCarrier", parcelShopData?.carrier)
        );
        customFields.push(
            shapeCustomField("address_CF_ParcelTypeCode", parcelShopData?.parcelTypeCode)
        );
    }
    return customFields;
};

export const shapeAddressCustomFields = (address, parcelShopData = null) => {
    const customFields = [];

    if (address?.phoneticFirstName) {
        customFields.push(
            shapeCustomField("address_CF_PhoneticFirstName", address?.phoneticFirstName)
        );
    }

    if (address?.phoneticLastName) {
        customFields.push(
            shapeCustomField("address_CF_PhoneticLastName", address?.phoneticLastName)
        );
    }

    if (address?.landmark) {
        customFields.push(shapeCustomField("address_CF_Landmark", address?.landmark));
    }

    if (address?.deliveryInstructions) {
        customFields.push(
            shapeCustomField("address_CF_DeliveryInstructions", address?.deliveryInstructions)
        );
    }

    if (address.isCompany) {
        customFields.push(shapeCustomField("address_CF_IsCompany", Boolean(address?.isCompany)));
    }

    const vatIdCustomField = shapeCustomField("address_CF_VatId", address?.vatNumber);
    if (vatIdCustomField) {
        customFields.push(vatIdCustomField);
    }

    if (activeSiteConfig.checkout?.hasDHLExtraFields && address?.customerNumber) {
        customFields.push(shapeCustomField("address_CF_CustomerNumber", address?.customerNumber));
    }
    if (parcelShopData) {
        const shapedParcelShopData = shapeParcelShopCustomFields(parcelShopData);
        customFields.push(...shapedParcelShopData);
    }

    return customFields;
};

const mapToMoney = (value: BaseMoney, locale: string): MoneyProps => {
    if (!value) return null;

    return {
        locale: locale,
        data: {
            type: value.type,
            fractionDigits: value.fractionDigits,
            currencyCode: value.currencyCode,
            centAmount: value.centAmount,
        },
    };
};
